import React, { FC } from 'react';
import styled from 'styled-components';

import UpCaret from '../../../assets/icons/caret-up.svg';
import { colors, TABLET_BREAKPOINT, MOBILE_BREAKPOINT } from '../../constants';
import { Logout } from '../../utilities/identity';
import { getJWTFromLocalStorage } from '../../utilities/jwt';

import { StyledNavProps } from './NavStyles';

// Action Pill
// Pill shaped button that lets client sign-in, see offer at a glance, etc.

export interface ActionPillProps {
  onSignOutClick?: Function;
}

// Styles -----------------------------------------------------------------------------------------
const ActionButton = styled.button`
  height: 2.25rem;
  min-width: 2.25rem;
  border-radius: 1.5rem;
  display: flex;
  justify-content: space-around;
  align-items: center;
  background-color: ${colors.UI_SECONDARY};
  cursor: pointer;
`;
ActionButton.displayName = 'ActionButton';

const ButtonText = styled.p`
  padding: 0 1rem;
  text-align: center;
  font-size: 0.75rem;
  color: ${colors.TEXT_PRIMARY};
`;
ButtonText.displayName = 'ButtonText';

const Caret = styled(UpCaret)`
  margin: 0 0.875rem 0 0;
  color: ${colors.TEXT_PRIMARY};
  transform: rotate(180deg);
`;
Caret.displayName = 'Caret';

export const ActionPillContainer = styled.div<StyledNavProps>`
  position: absolute;
  right: 2rem;
  ${(p: StyledNavProps) =>
    p.showProgressBar &&
    `
    @media (max-width: ${TABLET_BREAKPOINT}) {
      top: 1rem;
      margin: 0px 0px 2.125rem;
    }
  `};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    right: 1rem;
  }
`;
ActionPillContainer.displayName = 'ActionPillContainer';

// Component --------------------------------------------------------------------------------------
export const ActionPill: FC<ActionPillProps> = ({ onSignOutClick }) => {
  // Try to grab the jwt from local storage
  const jwt = getJWTFromLocalStorage();

  const onSignOut = () => {
    // If we were provided a signout callback
    if (onSignOutClick) {
      // Call it to coordinate with parent
      onSignOutClick();
    }
    Logout('/credit-card');
  };

  const onSignIn = () => {
    // Send them to the six digit code page with no return url
    window.location.assign('/auth/email-code');
  };

  // If there's not a jwt, show the sign in button
  if (!jwt) {
    return (
      <ActionButton
        id="action-pill-button"
        aria-label="Log in to your account"
        role="link"
        onClick={onSignIn}
      >
        <ButtonText id="sign-in">Log&nbsp;in</ButtonText>
      </ActionButton>
    );
  }
  // Otherwise show the sign out button
  return (
    <ActionButton
      id="action-pill-sign-out-button"
      onClick={onSignOut}
      aria-label="Sign out of your account"
      role="link"
      data-testid="sign-out"
    >
      <ButtonText id="sign-out">Sign&nbsp;out</ButtonText>
    </ActionButton>
  );
};

ActionPill.defaultProps = {
  onSignOutClick: undefined,
};
